import React, { useState } from 'react';
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { useSendInviteMutation } from 'src/api/mutations';
import { validateEmail } from 'src/utils/email';

type InviteRow = {
  email: string;
  companyName: string;
  status: 'notSent' | 'invited' | 'accepted';
};

const statuses = {
  notSent: 'Not sent',
  invited: 'Invited',
  rejected: 'Rejected',
  accepted: 'Accepted',
};

const statusColors = {
  notSent: 'gray',
  invited: 'yellow.600',
  accepted: 'icon.green',
};

export const AdminInviteUsers: React.FC = () => {
  const sendInviteMutation = useSendInviteMutation();
  const [rows, setRows] = useState<InviteRow[]>([
    {
      email: 'test@example.com',
      companyName: 'Test customer note',
      status: 'invited',
    },
    {
      email: 'test2@example.com',
      companyName: 'Test customer note 2',
      status: 'invited',
    },
    {
      email: 'test3@example.com',
      companyName: 'Test customer note 3',
      status: 'accepted',
    },
  ]);

  const [newEmail, setNewEmail] = useState<string>('');
  const [newCompanyName, setNewCompanyName] = useState<string>('');
  // rows are view-only now

  const handleSendNewInvite = () => {
    if (!validateEmail(newEmail) || newCompanyName.trim().length === 0) {
      return;
    }
    sendInviteMutation.mutate({
      email: newEmail,
      companyName: newCompanyName,
      role: 'admin',
    });
    setRows((prev) => [
      ...prev,
      {
        email: newEmail,
        companyName: newCompanyName,
        status: 'invited',
      },
    ]);
    setNewEmail('');
    setNewCompanyName('');
  };

  return (
    <Box p={10}>
      <Text fontSize="24px" fontWeight="bold" mb={4}>
        Invite new customers
      </Text>
      <Table variant="simple" size="md">
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>Email</Th>
            <Th>Customer</Th>
            <Th>Status</Th>
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>0</Td>
            <Td>
              <Input
                placeholder="name@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </Td>
            <Td>
              <Input
                placeholder="Company name"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
              />
            </Td>
            <Td>
              <Text color={statusColors.notSent}>{statuses.notSent}</Text>
            </Td>
            <Td>
              <Button
                buttonName="send-invite"
                bg="icon.green"
                onClick={handleSendNewInvite}
                isDisabled={
                  !(validateEmail(newEmail) && newCompanyName.trim().length > 0)
                }>
                Send invite
              </Button>
            </Td>
          </Tr>
          {rows.map((row, index) => {
            const isLocked = row.status !== 'notSent';
            return (
              <Tr key={index}>
                <Td>{index + 1}</Td>
                <Td>
                  <Input
                    placeholder="name@example.com"
                    value={row.email}
                    isDisabled={isLocked}
                  />
                </Td>
                <Td>
                  <Input
                    placeholder="Customer note"
                    value={row.companyName}
                    isDisabled={isLocked}
                  />
                </Td>
                <Td>
                  <Text color={statusColors[row.status]}>
                    {statuses[row.status]}
                  </Text>
                </Td>
                <Td />
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
